import "./VarietiesPage.css";

import { zodResolver } from "@hookform/resolvers/zod";
import { orderBy } from "firebase/firestore";
import isEqual from "lodash/isEqual";
import moment from "moment-timezone";
import papa from "papaparse";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Accordion,
    Alert,
    Button,
    Card,
    Col,
    Container,
    Dropdown,
    Form,
    Modal,
    OverlayTrigger,
    Row,
    Spinner,
    Tooltip,
} from "react-bootstrap";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { BsExclamationCircle } from "react-icons/bs";
import {
    FaAngleLeft,
    FaExclamationTriangle,
    FaPen,
    FaPlus,
    FaSave,
    FaTimes,
    FaTrash,
    FaUpload,
} from "react-icons/fa";
import { MdOutlineMoreVert } from "react-icons/md";
import {
    useBeforeUnload,
    useBlocker,
    useNavigate,
    useParams,
} from "react-router";
import Select, { createFilter } from "react-select";
import { z } from "zod";

import * as api from "../../api/api.jsx";
import {
    useBatchesCollection,
    useLocationDocument,
} from "../../api/firestoreWrappers.generated.jsx";
import DatePicker from "../../components/DatePicker";
import AreaBuilderNumberFormGroup from "../../components/FormGroups/AreaBuilderFormGroups.jsx";
import TextFormGroup from "../../components/FormGroups/TextFormGroup.jsx";
import Header from "../../components/Header/Header.jsx";
import { Title } from "../../components/Layout/Layout.jsx";
import CorvusMap from "../../components/Map/index.jsx";
import urls from "../../urls.jsx";
import {
    ConfigDownloadCSVButton,
    ConfigUploadCSVButton,
} from "../LocationSettingsPage/index.jsx";

const optionalCoordinateSchema = z.preprocess(
    (value) =>
        typeof value === "string" && value.trim() === "" ? undefined : value,
    z.coerce.number({ invalid_type_error: "Enter a valid number" }).optional(),
);

const batchSchema = z
    .object({
        area: z
            .object({
                min: z.object({
                    x: optionalCoordinateSchema,
                    y: optionalCoordinateSchema,
                }),
                max: z.object({
                    x: optionalCoordinateSchema,
                    y: optionalCoordinateSchema,
                }),
            })
            .superRefine((area, ctx) => {
                if (
                    area.min.x !== undefined &&
                    area.max.x !== undefined &&
                    area.min.x > area.max.x
                ) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ["min", "x"],
                        message: "xmin cannot be greater than xmax",
                    });
                }

                if (
                    area.min.y !== undefined &&
                    area.max.y !== undefined &&
                    area.min.y > area.max.y
                ) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ["min", "y"],
                        message: "ymin cannot be greater than ymax",
                    });
                }
            }),

        data: z
            .object({
                uuid: z.string(),
                name: z.string(),
            })
            .passthrough(),
    })
    .passthrough();

function createEmptyVariety() {
    return {
        area: {
            min: {
                x: undefined,
                y: undefined,
            },
            max: {
                x: undefined,
                y: undefined,
            },
        },
        data: {
            uuid: undefined,
            name: "New variety",
        },
    };
}

function timestampToDate(timestamp) {
    const date =
        timestamp instanceof Date
            ? timestamp
            : typeof timestamp?.toDate === "function"
              ? timestamp.toDate()
              : timestamp?.seconds != null
                ? new Date(
                      Number(timestamp.seconds) * 1000 +
                          Number(timestamp.nanoseconds || 0) / 1_000_000,
                  )
                : undefined;

    return date instanceof Date && !Number.isNaN(date.getTime())
        ? date
        : undefined;
}

function uiDateToLocationDateKey(date) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime()))
        return undefined;

    return moment([date.getFullYear(), date.getMonth(), date.getDate()]).format(
        "YYYY-MM-DD",
    );
}

function persistedCreatedToLocationDateKey(created, locationTimezone) {
    const persistedDate = timestampToDate(created);
    if (!persistedDate) return undefined;

    return moment(persistedDate).tz(locationTimezone).format("YYYY-MM-DD");
}

function locationDateKeyToUiDate(locationDateKey) {
    const calendarDate = moment(locationDateKey, "YYYY-MM-DD", true);
    if (!calendarDate.isValid()) return undefined;

    return new Date(
        calendarDate.year(),
        calendarDate.month(),
        calendarDate.date(),
    );
}

function persistedCreatedToUiDate(created, locationTimezone) {
    return locationDateKeyToUiDate(
        persistedCreatedToLocationDateKey(created, locationTimezone),
    );
}

function uiDateToPersistedCreated(date, locationTimezone) {
    const locationDateKey = uiDateToLocationDateKey(date);
    if (!locationDateKey) return undefined;

    const locationMidnight = moment.tz(
        locationDateKey,
        "YYYY-MM-DD",
        true,
        locationTimezone,
    );

    return locationMidnight.isValid() ? locationMidnight.toDate() : undefined;
}

function formatValidFromDate(created, locationTimezone) {
    return persistedCreatedToLocationDateKey(created, locationTimezone);
}

function formatBatchDateRange(batches, index, locationTimezone) {
    const startDate = persistedCreatedToLocationDateKey(
        batches[index]?.created,
        locationTimezone,
    );
    const today = moment.tz(locationTimezone).format("YYYY-MM-DD");
    const openEndedLabel = startDate > today ? "future" : "now";

    const endDate = batches[index + 1]
        ? moment
              .tz(
                  persistedCreatedToLocationDateKey(
                      batches[index + 1]?.created,
                      locationTimezone,
                  ),
                  "YYYY-MM-DD",
                  locationTimezone,
              )
              .subtract(1, "day")
              .format("YYYY-MM-DD")
        : openEndedLabel;
    if (startDate !== endDate) {
        return `${startDate} — ${endDate}`;
    }

    return startDate;
}

function batchesToCSV(batches) {
    const batchesToParse = batches.map((batch) => {
        return {
            xmin: batch.area?.min?.x,
            xmax: batch.area?.max?.x,
            ymin: batch.area?.min?.y,
            ymax: batch.area?.max?.y,
            ...(batch?.data || { uuid: "", name: "" }),
        };
    });

    let BATCH_COLUMNS = ["uuid", "name", "xmin", "ymin", "xmax", "ymax"];
    if (batchesToParse.length === 0) return BATCH_COLUMNS.join(",");

    const keysToAdd = Object.keys(batchesToParse[0]).filter(
        (x) => !BATCH_COLUMNS.includes(x),
    );
    BATCH_COLUMNS = [...BATCH_COLUMNS, ...keysToAdd];

    return papa.unparse(batchesToParse, {
        columns: BATCH_COLUMNS,
        header: true,
    });
}

function hasDuplicateValidFromDate(
    date,
    batches,
    excludedBatchId,
    locationTimezone,
) {
    const locationDateKey = uiDateToLocationDateKey(date);
    if (!locationDateKey) return false;

    return batches.some(
        (batch) =>
            batch.id !== excludedBatchId &&
            persistedCreatedToLocationDateKey(
                batch.created,
                locationTimezone,
            ) === locationDateKey,
    );
}

function getImportValidFromDateError(
    date,
    batches,
    excludedBatchId,
    locationTimezone,
) {
    if (!date) return undefined;
    if (!uiDateToLocationDateKey(date))
        return "Valid from must be a valid date.";

    return hasDuplicateValidFromDate(
        date,
        batches,
        excludedBatchId,
        locationTimezone,
    )
        ? "Another variety set already uses this Valid from date."
        : undefined;
}

function createBatchesSchema(batches, selectedBatchId, locationTimezone) {
    return z
        .object({
            batches: z.array(batchSchema),
            validFromDate: z
                .date({
                    required_error: "Valid from is required.",
                    invalid_type_error: "Valid from must be a valid date.",
                })
                .refine((date) => !Number.isNaN(date.getTime()), {
                    message: "Valid from must be a valid date.",
                }),
        })
        .passthrough()
        .superRefine(({ validFromDate }, ctx) => {
            if (
                hasDuplicateValidFromDate(
                    validFromDate,
                    batches,
                    selectedBatchId,
                    locationTimezone,
                )
            ) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["validFromDate"],
                    message:
                        "Another variety set already uses this Valid from date.",
                });
            }
        });
}

function parseVarietyCsv(csv) {
    const batchColumns = ["uuid", "name", "xmin", "ymin", "xmax", "ymax"];

    api.validateCsvColumns(csv, batchColumns, false);

    const { data: parsedBatches, errors } = papa.parse(csv, {
        columns: batchColumns,
        header: true,
        skipEmptyLines: true,
    });
    if (errors.length > 0) {
        throw new Error(errors.map((error) => error.message).join("\n"));
    }

    return parsedBatches.map((batch) => {
        const { xmin, ymin, xmax, ymax, ...data } = batch;

        return {
            area: {
                min: {
                    x: xmin,
                    y: ymin,
                },
                max: {
                    x: xmax,
                    y: ymax,
                },
            },
            data: { ...data },
        };
    });
}

function parseVarietyExportFilename(filename, batches) {
    if (typeof filename !== "string") return {};
    if (!filename.startsWith("variety_") || !filename.endsWith(".csv"))
        return {};

    const filenameStem = filename.slice(0, -4);
    const dateMatch = filenameStem.match(/_(\d{4}-\d{2}-\d{2})(?: ?\(\d+\))?$/);
    const dateKey = dateMatch?.[1];
    const filenameDate =
        dateKey && moment(dateKey, "YYYY-MM-DD", true).isValid()
            ? locationDateKeyToUiDate(dateKey)
            : undefined;
    const matchingBatches = dateKey
        ? batches.filter((batch) =>
              filenameStem
                  .slice(0, -dateMatch[0].length)
                  .endsWith(`_${batch.id}`),
          )
        : [];

    return {
        sourceBatchId:
            matchingBatches.length === 1 ? matchingBatches[0].id : undefined,
        filenameDate,
    };
}

function getImportValidationMessages(error) {
    if (error instanceof z.ZodError) {
        return error.issues.map((issue) => {
            const path =
                issue.path.length > 0 ? `${issue.path.join(".")}: ` : "";
            return `${path}${issue.message}`;
        });
    }

    return [error?.message || "The CSV file could not be imported."];
}

const VarietyName = React.memo(function VarietyName({ control, index }) {
    const name = useWatch({ control, name: `batches.${index}.data.name` });

    return <h4 className="my-0 ms-1 me-2">{name}</h4>;
});

function normalizeVarietyForForm(batch) {
    const data = { ...batch.data };

    if (data.uuid === undefined && data.UUID !== undefined) {
        data.uuid = String(data.UUID);
        delete data.UUID;
    }

    return {
        ...batch,
        area: {
            min: {
                x: batch.area?.min?.x ?? undefined,
                y: batch.area?.min?.y ?? undefined,
            },
            max: {
                x: batch.area?.max?.x ?? undefined,
                y: batch.area?.max?.y ?? undefined,
            },
        },
        data,
    };
}

function normalizeBatchesForForm(batches) {
    return (batches ?? []).map(normalizeVarietyForForm);
}

function prepareBatchesForPersistence(batches) {
    return (batches ?? []).map((batch) => ({
        ...batch,
        area: {
            min: {
                x: batch.area?.min?.x ?? null,
                y: batch.area?.min?.y ?? null,
            },
            max: {
                x: batch.area?.max?.x ?? null,
                y: batch.area?.max?.y ?? null,
            },
        },
    }));
}

function hasCompleteCoordinates(batch) {
    return [
        batch.area?.min?.x,
        batch.area?.min?.y,
        batch.area?.max?.x,
        batch.area?.max?.y,
    ].every(
        (coordinate) =>
            coordinate !== undefined &&
            coordinate !== null &&
            coordinate !== "" &&
            Number.isFinite(Number(coordinate)),
    );
}

function batchesEqual(batch1, batch2, locationTimezone) {
    return (
        persistedCreatedToLocationDateKey(batch1?.created, locationTimezone) ===
            persistedCreatedToLocationDateKey(
                batch2?.created,
                locationTimezone,
            ) &&
        isEqual(
            normalizeBatchesForForm(batch1?.batches),
            normalizeBatchesForForm(batch2?.batches),
        )
    );
}

function hasDirtyFields(value) {
    if (value === true) return true;
    if (!value || typeof value !== "object") return false;

    return Object.values(value).some(hasDirtyFields);
}

function hasValidationErrors(value) {
    if (!value || typeof value !== "object") return false;
    if (typeof value.message === "string") return true;

    return Object.values(value).some(hasValidationErrors);
}

function getPendingActionMessage(pendingAction) {
    switch (pendingAction?.type) {
        case "selection":
            return "Do you want to save before switching to another variety set?";
        case "start-import":
            return "Do you want to save before importing another variety set?";
        default:
            return "Do you want to save before continuing?";
    }
}

export default function VarietiesPage() {
    const { organizationId, locationId } = useParams();
    const navigate = useNavigate();

    const [activeKey, setActiveKey] = useState(undefined);

    const [sidebarWidth, setSidebarWidth] = useState(() => {
        const saved = localStorage.getItem("sidebarWidth");
        return saved ? Number(saved) : 400;
    });
    const isResizing = useRef(false);
    const [isResizingState, setIsResizingState] = useState(false);
    const [isHoveringResize, setIsHoveringResize] = useState(false);

    const [location, locationLoading] = useLocationDocument(
        organizationId,
        locationId,
    );
    const locationTimezone = location?.timezone || "UTC";
    const [batches, batchesLoading] = useBatchesCollection(
        organizationId,
        locationId,
        [orderBy("created", "asc")],
    );
    const [selectedBatchId, setSelectedBatchId] = React.useState(null);
    const hasInitializedSelection = useRef(false);
    const selectedBatch = batches.find((batch) => batch.id === selectedBatchId);
    const [lastHoveredVariety, setLastHoveredVariety] = React.useState();

    const [pendingAction, setPendingAction] = React.useState();
    const [showDeleteModal, setShowDeleteModal] = React.useState();
    const [showImportModal, setShowImportModal] = React.useState();
    const [showImportDecisionModal, setShowImportDecisionModal] =
        React.useState();
    const [importValidationMessages, setImportValidationMessages] =
        React.useState();
    const [importVarieties, setImportVarieties] = React.useState();
    const [importSourceBatchId, setImportSourceBatchId] = React.useState();
    const [importMode, setImportMode] = React.useState();
    const [importValidFromDate, setImportValidFromDate] = React.useState();
    const [importFileRequest, setImportFileRequest] = React.useState(0);
    const handleImportFileRequestHandled = React.useCallback(() => {
        setImportFileRequest(0);
    }, []);
    const [pendingCreatedBatchId, setPendingCreatedBatchId] = React.useState();
    const [pendingUpdatedBatch, setPendingUpdatedBatch] = React.useState();

    const batchesSchema = React.useMemo(
        () => createBatchesSchema(batches, selectedBatchId, locationTimezone),
        [batches, selectedBatchId, locationTimezone],
    );
    const {
        control,
        getValues,
        handleSubmit,
        reset,
        setValue,
        trigger,
        formState: { errors, isSubmitting, dirtyFields },
    } = useForm({
        resolver: zodResolver(batchesSchema),
        mode: "onChange",
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "batches",
    });
    const watchedBatches = useWatch({ control, name: "batches" });
    const watchedValidFromDate = useWatch({ control, name: "validFromDate" });
    const importValidFromDateError = getImportValidFromDateError(
        importValidFromDate,
        batches,
        importMode === "update" ? importSourceBatchId : undefined,
        locationTimezone,
    );
    const draftBatch = {
        id: selectedBatchId,
        created: uiDateToPersistedCreated(
            watchedValidFromDate,
            locationTimezone,
        ),
        batches: watchedBatches,
    };
    const isDirty =
        selectedBatch != null &&
        !batchesEqual(draftBatch, selectedBatch, locationTimezone);
    const isFormInvalid = hasValidationErrors(errors);
    const varietyCountDifference =
        (watchedBatches?.length ?? 0) - (selectedBatch?.batches?.length ?? 0);
    const navigationBlocker = useBlocker(isDirty);
    const highlightedBatches =
        watchedBatches?.map((_, index) => index === lastHoveredVariety) || [];

    const batchOptions = React.useMemo(() => {
        if (locationLoading || !batches) return [];

        return batches
            .map((batch, index) => ({
                value: batch.id,
                label: formatBatchDateRange(batches, index, locationTimezone),
                created: batch.created,
            }))
            .sort((a, b) => b.created.toMillis() - a.created.toMillis());
    }, [batches, locationLoading, locationTimezone]);

    const resetDraftFromBatch = React.useCallback(
        (batch) => {
            if (!batch) return;

            const validFromDate = persistedCreatedToUiDate(
                batch.created,
                locationTimezone,
            );

            reset({
                batches: normalizeBatchesForForm(batch.batches),
                validFromDate,
            });
        },
        [locationTimezone, reset],
    );

    const applyBatchSelection = React.useCallback(
        (batchId) => {
            const batch = batches.find((batch) => batch.id === batchId);
            if (!batch) return;

            setSelectedBatchId(batch.id);
            resetDraftFromBatch(batch);
        },
        [batches, resetDraftFromBatch],
    );

    const clearImportState = () => {
        setShowImportModal(undefined);
        setShowImportDecisionModal(undefined);
        setImportValidationMessages(undefined);
        setImportVarieties(undefined);
        setImportSourceBatchId(undefined);
        setImportMode(undefined);
        setImportValidFromDate(undefined);
    };

    const startImport = () => {
        clearImportState();
        setImportFileRequest((request) => request + 1);
    };

    const handleImportUpload = async (csv, filename) => {
        try {
            const varieties = parseVarietyCsv(csv);
            const parseResult = z.array(batchSchema).safeParse(varieties);
            if (!parseResult.success) {
                setImportValidationMessages(
                    getImportValidationMessages(parseResult.error),
                );
                return;
            }

            const { sourceBatchId, filenameDate } = parseVarietyExportFilename(
                filename,
                batches,
            );
            const existingBatchId = sourceBatchId;

            setImportVarieties(parseResult.data);
            setImportSourceBatchId(existingBatchId);
            setImportValidFromDate(filenameDate);

            if (existingBatchId) {
                setShowImportDecisionModal(true);
            } else {
                setImportMode("create");
                setShowImportModal(true);
            }
        } catch (error) {
            setImportValidationMessages(getImportValidationMessages(error));
        }
    };

    const chooseImportMode = (mode) => {
        setImportMode(mode);
        setShowImportDecisionModal(false);
        setShowImportModal(true);
    };

    const completeImport = async () => {
        if (
            !importVarieties ||
            !uiDateToLocationDateKey(importValidFromDate) ||
            importValidFromDateError
        )
            return;

        const importedBatch = {
            created: uiDateToPersistedCreated(
                importValidFromDate,
                locationTimezone,
            ),
            batches: prepareBatchesForPersistence(importVarieties),
        };

        if (importMode === "update") {
            await api.updateBatch(
                organizationId,
                locationId,
                importSourceBatchId,
                importedBatch,
            );
            setSelectedBatchId(importSourceBatchId);
            reset({
                batches: importVarieties,
                validFromDate: importValidFromDate,
            });
            setPendingUpdatedBatch({
                id: importSourceBatchId,
                ...importedBatch,
            });
            clearImportState();
            return;
        }

        const batchId = await api.createBatch(
            organizationId,
            locationId,
            importedBatch,
        );
        clearImportState();
        setPendingCreatedBatchId(batchId);
    };

    React.useEffect(() => {
        if (
            hasInitializedSelection.current ||
            batchesLoading ||
            locationLoading
        )
            return;

        hasInitializedSelection.current = true;
        if (selectedBatchId === null && batchOptions.length > 0) {
            applyBatchSelection(batchOptions[0].value);
        }
    }, [
        applyBatchSelection,
        batchOptions,
        batchesLoading,
        locationLoading,
        selectedBatchId,
    ]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: Revalidate the date when any input used by its validation rules changes.
    React.useEffect(() => {
        trigger("validFromDate");
    }, [batches, locationTimezone, selectedBatchId, trigger]);

    React.useEffect(() => {
        if (!pendingCreatedBatchId) return;

        const createdBatch = batches.find(
            (batch) => batch.id === pendingCreatedBatchId,
        );
        if (!createdBatch) return;

        applyBatchSelection(createdBatch.id);
        setPendingCreatedBatchId(undefined);
    }, [applyBatchSelection, batches, pendingCreatedBatchId]);

    React.useEffect(() => {
        if (!pendingUpdatedBatch) return;

        const updatedBatch = batches.find(
            (batch) => batch.id === pendingUpdatedBatch.id,
        );
        if (
            !updatedBatch ||
            !batchesEqual(updatedBatch, pendingUpdatedBatch, locationTimezone)
        )
            return;

        const currentDraft = {
            id: selectedBatchId,
            created: uiDateToPersistedCreated(
                getValues("validFromDate"),
                locationTimezone,
            ),
            batches: getValues("batches"),
        };
        if (batchesEqual(currentDraft, pendingUpdatedBatch, locationTimezone)) {
            setSelectedBatchId(updatedBatch.id);
            resetDraftFromBatch(updatedBatch);
        }
        setPendingUpdatedBatch(undefined);
    }, [
        batches,
        getValues,
        locationTimezone,
        pendingUpdatedBatch,
        resetDraftFromBatch,
        selectedBatchId,
    ]);

    const onSubmit = async (formValues) => {
        await api.updateBatch(organizationId, locationId, selectedBatchId, {
            created: uiDateToPersistedCreated(
                formValues.validFromDate,
                locationTimezone,
            ),
            batches: prepareBatchesForPersistence(formValues.batches),
        });

        reset(formValues);
    };

    const performAction = (action) => {
        if (!action) return;

        if (action.type === "selection") {
            applyBatchSelection(action.batchId);
            return;
        }

        if (action.type === "start-import") {
            startImport();
        }
    };

    const requestAction = (action) => {
        if (!isDirty) {
            performAction(action);
            return;
        }

        setPendingAction(action);
    };

    const requestBatchSelection = (batchId) => {
        if (batchId === selectedBatchId) return;

        requestAction({ type: "selection", batchId });
    };

    const requestImport = () => {
        requestAction({ type: "start-import" });
    };

    const discardChanges = () => {
        resetDraftFromBatch(selectedBatch);
    };

    const clearPendingAction = () => {
        setPendingAction(undefined);
    };

    const keepEditing = () => {
        if (navigationBlocker.state === "blocked") {
            navigationBlocker.reset();
        }
        clearPendingAction();
    };

    const discardChangesAndContinue = () => {
        const action = pendingAction;

        discardChanges();
        clearPendingAction();
        if (navigationBlocker.state === "blocked") {
            navigationBlocker.proceed();
            return;
        }
        performAction(action);
    };

    const saveChangesAndContinue = handleSubmit(async (formValues) => {
        const action = pendingAction;

        await onSubmit(formValues);
        clearPendingAction();
        if (navigationBlocker.state === "blocked") {
            navigationBlocker.proceed();
            return;
        }
        performAction(action);
    });

    useBeforeUnload(
        React.useCallback(
            (event) => {
                if (!isDirty) return;

                event.preventDefault();
                event.returnValue = "";
            },
            [isDirty],
        ),
    );

    useEffect(() => {
        localStorage.setItem("sidebarWidth", sidebarWidth);
    }, [sidebarWidth]);

    const startResizing = (e) => {
        e.preventDefault();
        e.stopPropagation();

        isResizing.current = true;
        setIsResizingState(true);

        document.body.style.userSelect = "none";
    };

    const stopResizing = useCallback(() => {
        isResizing.current = false;
        setIsResizingState(false);

        document.body.style.userSelect = "";
    }, []);

    const resize = useCallback((e) => {
        if (!isResizing.current) return;

        const minWidth = 300;
        const maxWidth = Math.min(900, window.innerWidth - 100);
        const newWidth = Math.min(maxWidth, Math.max(minWidth, e.clientX));
        setSidebarWidth(newWidth);
    }, []);

    function newVariety() {
        const undefine = (obj) =>
            Object.fromEntries(
                Object.entries(obj).map(([k, v]) => [
                    k,
                    v && typeof v === "object" ? undefine(v) : undefined,
                ]),
            );

        const emptyBatch = fields[0]
            ? undefine(fields[0])
            : createEmptyVariety();
        emptyBatch.data.name = "New variety";

        append(emptyBatch);
    }

    function deleteVariety(index) {
        remove(index);
    }

    useEffect(() => {
        const handleMouseMove = (e) => resize(e);
        const handleMouseUp = () => stopResizing();

        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);

        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, [resize, stopResizing]);

    const CustomToggle = React.forwardRef(({ onClick }, ref) => (
        <button
            type="button"
            ref={ref}
            onClick={(e) => {
                e.preventDefault();
                onClick(e);
            }}
            className="p-0 bg-transparent border-0 d-flex h-100"
        >
            <MdOutlineMoreVert size={20} />
        </button>
    ));
    CustomToggle.displayName = "CustomToggle";
    return (
        <>
            <Modal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
            >
                <Modal.Body>
                    <div className="text-danger">
                        Are you sure you want to delete the variety set valid
                        from{" "}
                        <strong className="text">
                            {formatValidFromDate(
                                selectedBatch?.created,
                                locationTimezone,
                            ) || ""}
                        </strong>
                        ?
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-secondary"
                        onClick={() => setShowDeleteModal(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        type="submit"
                        className="mx-2"
                        onClick={async () => {
                            const selectedBatchIndex = batches.findIndex(
                                (batch) => batch.id === selectedBatchId,
                            );
                            const nextBatchId =
                                selectedBatchIndex === -1
                                    ? undefined
                                    : (batches[selectedBatchIndex + 1]?.id ??
                                      batches[selectedBatchIndex - 1]?.id);

                            await api.deleteBatch(
                                organizationId,
                                locationId,
                                selectedBatchId,
                            );

                            if (nextBatchId) {
                                applyBatchSelection(nextBatchId);
                            } else {
                                setSelectedBatchId(null);
                            }

                            setShowDeleteModal(false);
                        }}
                    >
                        <FaTrash className="mb-1 me-1" />
                        {"Delete"}
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal show={!!importValidationMessages} onHide={clearImportState}>
                <Modal.Header closeButton>
                    <Modal.Title>Import validation error</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ul className="mb-0">
                        {importValidationMessages?.map((message, index) => (
                            // biome-ignore lint/suspicious/noArrayIndexKey: Validation messages may contain duplicates and have no stable identifier.
                            <li key={index}>{message}</li>
                        ))}
                    </ul>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-secondary"
                        onClick={clearImportState}
                    >
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal show={showImportDecisionModal} onHide={clearImportState}>
                <Modal.Header closeButton>
                    <Modal.Title>Existing variety set found</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    This file refers to an existing variety set. Do you want to
                    update it or add the imported varieties as a new variety
                    set?
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-secondary"
                        onClick={clearImportState}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="outline-primary"
                        onClick={() => chooseImportMode("create")}
                    >
                        Add as new
                    </Button>
                    <Button
                        variant="success"
                        onClick={() => chooseImportMode("update")}
                    >
                        Update existing
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal show={showImportModal} onHide={clearImportState}>
                <Modal.Header closeButton>
                    <Modal.Title>Import variety set</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="">
                        When is this variety set valid from?
                        <Col md={4} className="p-0 pe-md-1 mt-2">
                            <DatePicker
                                value={importValidFromDate}
                                onChange={setImportValidFromDate}
                                placeholder="Set a date"
                                clearable={false}
                            />
                            {importValidFromDateError && (
                                <Form.Text className="text-danger d-block">
                                    {importValidFromDateError}
                                </Form.Text>
                            )}
                        </Col>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-secondary"
                        onClick={clearImportState}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="success"
                        type="submit"
                        disabled={
                            !uiDateToLocationDateKey(importValidFromDate) ||
                            !!importValidFromDateError
                        }
                        className="x-2"
                        onClick={completeImport}
                    >
                        <FaUpload className="mb-1 me-2" />
                        {importMode === "update" ? "Update" : "Import"}
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={!!pendingAction || navigationBlocker.state === "blocked"}
                onHide={keepEditing}
            >
                <Modal.Body>
                    <div className="d-flex align-items-start gap-3">
                        <FaExclamationTriangle className="text-warning fs-3 mt-1" />
                        <div>
                            <div className="fw-bold fs-4">Unsaved changes</div>
                            <div className="text-muted">
                                {navigationBlocker.state === "blocked"
                                    ? "Do you want to save before leaving this page?"
                                    : getPendingActionMessage(pendingAction)}
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-primary" onClick={keepEditing}>
                        <FaPen className="me-2" />
                        Keep editing
                    </Button>
                    <Button
                        variant="outline-danger"
                        onClick={discardChangesAndContinue}
                    >
                        <FaTimes className="me-2" />
                        Discard
                    </Button>
                    <Button
                        variant="success"
                        disabled={isSubmitting || !isDirty || isFormInvalid}
                        onClick={saveChangesAndContinue}
                        className="ms-2"
                    >
                        <FaSave className="mb-1 me-1" />
                        {isSubmitting ? "Saving..." : "Save"}
                    </Button>
                </Modal.Footer>
            </Modal>

            <title>Varieties | MyCorvus</title>

            {/* Small screens */}
            <div className="d-sm-none vh-100 d-flex justify-content-center align-items-center text-center p-3">
                <div>
                    <h4>This page requires a larger screen</h4>
                    <p>
                        Please use a tablet or desktop to access the varieties
                        page.
                    </p>
                    <Button onClick={() => navigate(-1)}>Go Back</Button>
                </div>
            </div>

            {/* Large screens */}
            <div
                className="d-none d-sm-flex flex-column varieties-page"
                style={{ height: "100vh" }}
            >
                <Header style={{ maxWidth: "850px" }} />

                <Container
                    fluid
                    className="p-0 d-flex"
                    style={{
                        flexGrow: 1,
                        overflow: "hidden",
                        position: "relative",
                    }}
                >
                    <div
                        style={{
                            width: sidebarWidth,
                            position: "relative",
                            display: "flex",
                            flexDirection: "column",
                            flexShrink: 0,
                        }}
                    >
                        <div
                            style={{
                                flex: 1,
                                overflowY: "auto",
                                pointerEvents: isResizingState
                                    ? "none"
                                    : "auto",
                            }}
                        >
                            <div className="pb-5">
                                {batchesLoading ||
                                locationLoading ||
                                (!selectedBatch && !!batches.length) ? (
                                    <div className="d-flex justify-content-center pt-5">
                                        <Spinner
                                            animation="border"
                                            role="status"
                                        >
                                            <span className="visually-hidden">
                                                Loading...
                                            </span>
                                        </Spinner>
                                    </div>
                                ) : (
                                    <>
                                        <Title
                                            title={
                                                <div className="d-inline-flex align-items-center">
                                                    <Button
                                                        variant="link"
                                                        className="p-0 me-2"
                                                        onClick={() =>
                                                            navigate(
                                                                urls.locationSettings(
                                                                    organizationId,
                                                                    locationId,
                                                                ),
                                                            )
                                                        }
                                                    >
                                                        <FaAngleLeft
                                                            size={30}
                                                        />
                                                    </Button>
                                                    Varieties
                                                </div>
                                            }
                                        >
                                            <div className="ms-md-2">
                                                <ConfigUploadCSVButton
                                                    disabled={false}
                                                    addFilename={true}
                                                    onStart={requestImport}
                                                    startUploadRequest={
                                                        importFileRequest
                                                    }
                                                    onStartUploadHandled={
                                                        handleImportFileRequestHandled
                                                    }
                                                    onUpload={
                                                        handleImportUpload
                                                    }
                                                    buttonText="Import"
                                                />
                                            </div>
                                        </Title>

                                        {(batches || []).length === 0 ? (
                                            <>
                                                <Alert
                                                    className="mx-4"
                                                    variant="warning"
                                                >
                                                    No variety sets in this
                                                    location
                                                </Alert>
                                                <div
                                                    className="mx-4 mb-3"
                                                    style={{
                                                        width: "fit-content",
                                                    }}
                                                >
                                                    <ConfigDownloadCSVButton
                                                        name={`variety_${organizationId}_${locationId}_new_${moment().tz(locationTimezone).format("YYYY-MM-DD")}`}
                                                        disabled={false}
                                                        onDownload={async () =>
                                                            batchesToCSV([])
                                                        }
                                                        buttonText="Export empty variety set"
                                                    />
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="mx-2">
                                                    <Form.Label htmlFor="variety-set-selector">
                                                        Select variety set
                                                    </Form.Label>
                                                    <div className="d-flex gap-2 align-items-start">
                                                        <div className="flex-grow-1">
                                                            <Select
                                                                inputId="variety-set-selector"
                                                                options={
                                                                    batchOptions
                                                                }
                                                                value={batchOptions.find(
                                                                    (e) =>
                                                                        e.value ===
                                                                        selectedBatchId,
                                                                )}
                                                                onChange={(
                                                                    option,
                                                                ) =>
                                                                    requestBatchSelection(
                                                                        option?.value,
                                                                    )
                                                                }
                                                                placeholder="Select variety set"
                                                                filterOption={createFilter(
                                                                    {
                                                                        stringify:
                                                                            (
                                                                                option,
                                                                            ) =>
                                                                                option.label,
                                                                    },
                                                                )} // Only match labels when typing
                                                                styles={{
                                                                    menu: (
                                                                        base,
                                                                    ) => ({
                                                                        ...base,
                                                                        zIndex: 9999,
                                                                    }),
                                                                }}
                                                            />
                                                        </div>
                                                        <OverlayTrigger
                                                            overlay={
                                                                <Tooltip>
                                                                    Delete
                                                                    variety set
                                                                </Tooltip>
                                                            }
                                                        >
                                                            <Button
                                                                variant="outline-danger"
                                                                aria-label="Delete variety set"
                                                                onClick={() =>
                                                                    setShowDeleteModal(
                                                                        true,
                                                                    )
                                                                }
                                                            >
                                                                <FaTrash />
                                                            </Button>
                                                        </OverlayTrigger>
                                                    </div>
                                                </div>

                                                <hr className="mx-2 my-4" />

                                                <Form
                                                    onSubmit={handleSubmit(
                                                        onSubmit,
                                                        (errors) =>
                                                            console.log(errors),
                                                    )}
                                                    className="mt-2"
                                                >
                                                    <div className="mx-2 mb-3">
                                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                                            <Button
                                                                variant="success"
                                                                disabled={
                                                                    isSubmitting ||
                                                                    !isDirty ||
                                                                    isFormInvalid
                                                                }
                                                                type="submit"
                                                            >
                                                                <FaSave className="mb-1 me-1" />
                                                                {isSubmitting
                                                                    ? "Saving..."
                                                                    : "Save"}
                                                            </Button>
                                                            <Button
                                                                type="button"
                                                                variant="outline-danger"
                                                                disabled={
                                                                    !isDirty
                                                                }
                                                                onClick={
                                                                    discardChanges
                                                                }
                                                            >
                                                                <FaTimes className="mb-1 me-1" />
                                                                Discard changes
                                                            </Button>
                                                            <ConfigDownloadCSVButton
                                                                name={`variety_${organizationId}_${locationId}_${selectedBatchId}_${formatValidFromDate(draftBatch?.created, locationTimezone) || ""}`}
                                                                disabled={false}
                                                                onDownload={async () =>
                                                                    batchesToCSV(
                                                                        draftBatch?.batches ||
                                                                            [],
                                                                    )
                                                                }
                                                                buttonText="Export"
                                                            />
                                                        </div>
                                                        {isFormInvalid && (
                                                            <div className="mt-2 small">
                                                                <div className="d-flex align-items-start text-danger">
                                                                    <FaExclamationTriangle className="me-2 mt-1 flex-shrink-0" />
                                                                    <span>
                                                                        Fix the
                                                                        errors
                                                                        marked
                                                                        below
                                                                        before
                                                                        saving.
                                                                    </span>
                                                                </div>

                                                                <div className="d-flex align-items-start text-muted mt-1">
                                                                    <BsExclamationCircle className="me-2 mt-1 flex-shrink-0" />
                                                                    <span>
                                                                        For
                                                                        larger
                                                                        corrections,
                                                                        you can
                                                                        also
                                                                        export,
                                                                        edit and
                                                                        import.
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <Row
                                                        className={`mx-2 gx-0 ${dirtyFields.validFromDate ? "variety-dirty-field" : ""}`}
                                                    >
                                                        <Form.Label
                                                            column
                                                            md={4}
                                                            className="px-2"
                                                        >
                                                            Valid from:
                                                        </Form.Label>

                                                        <Col
                                                            md={8}
                                                            className="p-0 pe-md-1"
                                                        >
                                                            <Controller
                                                                name="validFromDate"
                                                                control={
                                                                    control
                                                                }
                                                                render={({
                                                                    field,
                                                                }) => (
                                                                    <DatePicker
                                                                        value={
                                                                            field.value
                                                                        }
                                                                        onChange={
                                                                            field.onChange
                                                                        }
                                                                        placeholder="Set a date"
                                                                        clearable={
                                                                            false
                                                                        }
                                                                    />
                                                                )}
                                                            />
                                                            {errors
                                                                .validFromDate
                                                                ?.message && (
                                                                <Form.Text className="text-danger d-block">
                                                                    {
                                                                        errors
                                                                            .validFromDate
                                                                            .message
                                                                    }
                                                                </Form.Text>
                                                            )}
                                                        </Col>
                                                    </Row>

                                                    {varietyCountDifference !==
                                                        0 && (
                                                        <div className="mx-2 mb-2 text-muted">
                                                            {Math.abs(
                                                                varietyCountDifference,
                                                            )}{" "}
                                                            {Math.abs(
                                                                varietyCountDifference,
                                                            ) === 1
                                                                ? "variety"
                                                                : "varieties"}{" "}
                                                            {varietyCountDifference >
                                                            0
                                                                ? "added"
                                                                : "removed"}
                                                        </div>
                                                    )}

                                                    <Card className="m-2 py-2">
                                                        <Accordion
                                                            alwaysOpen
                                                            id="accordion"
                                                            activeKey={
                                                                activeKey
                                                            }
                                                            onSelect={(
                                                                eventKey,
                                                            ) => {
                                                                if (
                                                                    isResizingState
                                                                )
                                                                    return;
                                                                setActiveKey(
                                                                    eventKey,
                                                                );
                                                            }}
                                                        >
                                                            {fields.map(
                                                                (
                                                                    field,
                                                                    index,
                                                                ) => {
                                                                    const isVarietyDirty =
                                                                        hasDirtyFields(
                                                                            dirtyFields
                                                                                .batches?.[
                                                                                index
                                                                            ],
                                                                        );
                                                                    const hasVarietyValidationErrors =
                                                                        hasValidationErrors(
                                                                            errors
                                                                                .batches?.[
                                                                                index
                                                                            ],
                                                                        );

                                                                    return (
                                                                        <Accordion.Item
                                                                            key={
                                                                                field.id
                                                                            }
                                                                            className="border-0 pe-2"
                                                                            eventKey={String(
                                                                                index,
                                                                            )}
                                                                        >
                                                                            <div className="d-flex">
                                                                                <style>
                                                                                    {`.accordion-button::after {margin-top: 0.5rem;}`}{" "}
                                                                                </style>
                                                                                <Accordion.Button
                                                                                    className={`pt-0 pe-0 ps-2 d-flex flex-grow-1 ${index > 0 ? "border-top" : ""}`}
                                                                                    onMouseEnter={() =>
                                                                                        setLastHoveredVariety(
                                                                                            index,
                                                                                        )
                                                                                    }
                                                                                    onMouseLeave={() =>
                                                                                        setLastHoveredVariety()
                                                                                    }
                                                                                    style={{
                                                                                        border: "none",
                                                                                        boxShadow:
                                                                                            "none",
                                                                                        backgroundColor:
                                                                                            "white",
                                                                                        alignItems:
                                                                                            "flex-start",
                                                                                    }}
                                                                                >
                                                                                    <VarietyName
                                                                                        control={
                                                                                            control
                                                                                        }
                                                                                        index={
                                                                                            index
                                                                                        }
                                                                                    />
                                                                                    {isVarietyDirty && (
                                                                                        <FaPen
                                                                                            className="text-muted align-self-center ms-1"
                                                                                            size={
                                                                                                14
                                                                                            }
                                                                                        />
                                                                                    )}
                                                                                    {hasVarietyValidationErrors && (
                                                                                        <FaExclamationTriangle
                                                                                            className="text-danger align-self-center ms-1"
                                                                                            size={
                                                                                                14
                                                                                            }
                                                                                        />
                                                                                    )}
                                                                                </Accordion.Button>

                                                                                <Dropdown
                                                                                    align="end"
                                                                                    className="align-self-start mt-2"
                                                                                    onClick={(
                                                                                        e,
                                                                                    ) =>
                                                                                        e.stopPropagation()
                                                                                    }
                                                                                >
                                                                                    <Dropdown.Toggle
                                                                                        as={
                                                                                            CustomToggle
                                                                                        }
                                                                                    />
                                                                                    <Dropdown.Menu>
                                                                                        <Dropdown.Item
                                                                                            className="text-danger d-flex align-items-center"
                                                                                            onClick={() =>
                                                                                                deleteVariety(
                                                                                                    index,
                                                                                                )
                                                                                            }
                                                                                        >
                                                                                            <FaTrash className="me-2" />
                                                                                            Delete
                                                                                        </Dropdown.Item>
                                                                                    </Dropdown.Menu>
                                                                                </Dropdown>
                                                                            </div>

                                                                            <Accordion.Body className="ps-3 pe-2">
                                                                                <div
                                                                                    className={
                                                                                        dirtyFields
                                                                                            .batches?.[
                                                                                            index
                                                                                        ]
                                                                                            ?.data
                                                                                            ?.name
                                                                                            ? "variety-dirty-field"
                                                                                            : ""
                                                                                    }
                                                                                >
                                                                                    <TextFormGroup
                                                                                        label="name"
                                                                                        name={`batches.${index}.data.name`}
                                                                                        control={
                                                                                            control
                                                                                        }
                                                                                        errors={
                                                                                            errors
                                                                                        }
                                                                                        setValue={
                                                                                            setValue
                                                                                        }
                                                                                        distribution={[
                                                                                            5,
                                                                                            7,
                                                                                        ]}
                                                                                    />
                                                                                </div>
                                                                                <div
                                                                                    className={
                                                                                        dirtyFields
                                                                                            .batches?.[
                                                                                            index
                                                                                        ]
                                                                                            ?.data
                                                                                            ?.uuid
                                                                                            ? "variety-dirty-field"
                                                                                            : ""
                                                                                    }
                                                                                >
                                                                                    <TextFormGroup
                                                                                        label="uuid"
                                                                                        name={`batches.${index}.data.uuid`}
                                                                                        control={
                                                                                            control
                                                                                        }
                                                                                        errors={
                                                                                            errors
                                                                                        }
                                                                                        setValue={
                                                                                            setValue
                                                                                        }
                                                                                        distribution={[
                                                                                            5,
                                                                                            7,
                                                                                        ]}
                                                                                    />
                                                                                </div>
                                                                                <div
                                                                                    className={
                                                                                        dirtyFields
                                                                                            .batches?.[
                                                                                            index
                                                                                        ]
                                                                                            ?.area
                                                                                            ?.min
                                                                                            ?.x
                                                                                            ? "variety-dirty-field"
                                                                                            : ""
                                                                                    }
                                                                                >
                                                                                    <AreaBuilderNumberFormGroup
                                                                                        label="xmin"
                                                                                        name={`batches.${index}.area.min.x`}
                                                                                        control={
                                                                                            control
                                                                                        }
                                                                                        errors={
                                                                                            errors
                                                                                        }
                                                                                        setValue={
                                                                                            setValue
                                                                                        }
                                                                                        unitText="m"
                                                                                        step={
                                                                                            0.01
                                                                                        }
                                                                                        distribution={[
                                                                                            5,
                                                                                            7,
                                                                                        ]}
                                                                                    />
                                                                                </div>
                                                                                <div
                                                                                    className={
                                                                                        dirtyFields
                                                                                            .batches?.[
                                                                                            index
                                                                                        ]
                                                                                            ?.area
                                                                                            ?.min
                                                                                            ?.y
                                                                                            ? "variety-dirty-field"
                                                                                            : ""
                                                                                    }
                                                                                >
                                                                                    <AreaBuilderNumberFormGroup
                                                                                        label="ymin"
                                                                                        name={`batches.${index}.area.min.y`}
                                                                                        control={
                                                                                            control
                                                                                        }
                                                                                        errors={
                                                                                            errors
                                                                                        }
                                                                                        setValue={
                                                                                            setValue
                                                                                        }
                                                                                        unitText="m"
                                                                                        step={
                                                                                            0.01
                                                                                        }
                                                                                        distribution={[
                                                                                            5,
                                                                                            7,
                                                                                        ]}
                                                                                    />
                                                                                </div>
                                                                                <div
                                                                                    className={
                                                                                        dirtyFields
                                                                                            .batches?.[
                                                                                            index
                                                                                        ]
                                                                                            ?.area
                                                                                            ?.max
                                                                                            ?.x
                                                                                            ? "variety-dirty-field"
                                                                                            : ""
                                                                                    }
                                                                                >
                                                                                    <AreaBuilderNumberFormGroup
                                                                                        label="xmax"
                                                                                        name={`batches.${index}.area.max.x`}
                                                                                        control={
                                                                                            control
                                                                                        }
                                                                                        errors={
                                                                                            errors
                                                                                        }
                                                                                        setValue={
                                                                                            setValue
                                                                                        }
                                                                                        unitText="m"
                                                                                        step={
                                                                                            0.01
                                                                                        }
                                                                                        distribution={[
                                                                                            5,
                                                                                            7,
                                                                                        ]}
                                                                                    />
                                                                                </div>
                                                                                <div
                                                                                    className={
                                                                                        dirtyFields
                                                                                            .batches?.[
                                                                                            index
                                                                                        ]
                                                                                            ?.area
                                                                                            ?.max
                                                                                            ?.y
                                                                                            ? "variety-dirty-field"
                                                                                            : ""
                                                                                    }
                                                                                >
                                                                                    <AreaBuilderNumberFormGroup
                                                                                        label="ymax"
                                                                                        name={`batches.${index}.area.max.y`}
                                                                                        control={
                                                                                            control
                                                                                        }
                                                                                        errors={
                                                                                            errors
                                                                                        }
                                                                                        setValue={
                                                                                            setValue
                                                                                        }
                                                                                        unitText="m"
                                                                                        step={
                                                                                            0.01
                                                                                        }
                                                                                        distribution={[
                                                                                            5,
                                                                                            7,
                                                                                        ]}
                                                                                    />
                                                                                </div>
                                                                                {Object.keys(
                                                                                    field.data,
                                                                                )
                                                                                    .length >
                                                                                    2 &&
                                                                                    Object.keys(
                                                                                        field.data,
                                                                                    )
                                                                                        .filter(
                                                                                            (
                                                                                                f,
                                                                                            ) =>
                                                                                                ![
                                                                                                    "name",
                                                                                                    "uuid",
                                                                                                ].includes(
                                                                                                    f,
                                                                                                ),
                                                                                        )
                                                                                        .map(
                                                                                            (
                                                                                                f,
                                                                                            ) => (
                                                                                                <div
                                                                                                    key={
                                                                                                        f
                                                                                                    }
                                                                                                    className={
                                                                                                        dirtyFields
                                                                                                            .batches?.[
                                                                                                            index
                                                                                                        ]
                                                                                                            ?.data?.[
                                                                                                            f
                                                                                                        ]
                                                                                                            ? "variety-dirty-field"
                                                                                                            : ""
                                                                                                    }
                                                                                                >
                                                                                                    <TextFormGroup
                                                                                                        label={`${f}`}
                                                                                                        name={`batches.${index}.data.${f}`}
                                                                                                        control={
                                                                                                            control
                                                                                                        }
                                                                                                        errors={
                                                                                                            errors
                                                                                                        }
                                                                                                        setValue={
                                                                                                            setValue
                                                                                                        }
                                                                                                        distribution={[
                                                                                                            5,
                                                                                                            7,
                                                                                                        ]}
                                                                                                    />
                                                                                                </div>
                                                                                            ),
                                                                                        )}

                                                                                <div className="w-100 d-flex justify-content-around">
                                                                                    <Button
                                                                                        variant="danger"
                                                                                        onClick={() =>
                                                                                            deleteVariety(
                                                                                                index,
                                                                                            )
                                                                                        }
                                                                                    >
                                                                                        <FaTrash className="mb-1 me-2" />
                                                                                        Delete
                                                                                        variety
                                                                                    </Button>
                                                                                </div>
                                                                            </Accordion.Body>
                                                                        </Accordion.Item>
                                                                    );
                                                                },
                                                            )}
                                                        </Accordion>
                                                    </Card>
                                                    <Button
                                                        onClick={() =>
                                                            newVariety()
                                                        }
                                                        className="mb-3 mt-1 ms-2"
                                                    >
                                                        <FaPlus className="mb-1 me-2" />
                                                        New variety
                                                    </Button>
                                                </Form>
                                            </>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Resize handle */}
                        {/* biome-ignore lint/a11y/noStaticElementInteractions: Resize handle uses mouse dragging rather than button-style activation. */}
                        <div
                            onMouseDown={startResizing}
                            onMouseEnter={() => setIsHoveringResize(true)}
                            onMouseLeave={() => setIsHoveringResize(false)}
                            style={{
                                pointerEvents: "auto",
                                width: "8px",
                                position: "absolute",
                                right: "-4px",
                                top: 0,
                                bottom: 0,
                                cursor: "col-resize",
                                zIndex: 10,
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            <div
                                style={{
                                    width: "2px",
                                    background: isResizingState
                                        ? "#666"
                                        : isHoveringResize
                                          ? "#999"
                                          : "#ccc",
                                }}
                            />
                        </div>
                    </div>

                    <div
                        style={{
                            flexGrow: 1,
                            flexShrink: 1,
                            minWidth: 0,
                            overflow: "hidden",
                        }}
                    >
                        <CorvusMap
                            organizationId={organizationId}
                            locationId={locationId}
                            areasToShow={{}}
                            batches={draftBatch?.batches?.flatMap(
                                (batch, batchIndex) =>
                                    hasCompleteCoordinates(batch)
                                        ? [
                                              {
                                                  ...batch,
                                                  rowKey: fields[batchIndex].id,
                                                  highlighted:
                                                      highlightedBatches[
                                                          batchIndex
                                                      ],
                                              },
                                          ]
                                        : [],
                            )}
                            inspect
                        />
                    </div>
                </Container>
            </div>
        </>
    );
}
